/**
 * Country-scoped bucketed metric series for ONE range (GET /dashboard/timeseries with a
 * campaign/provider/country scope). Same response shape as the dashboard series, so the
 * dashboard compare chart components consume it directly. A Tres Commas series
 * (provider=tres-commas) carries its numbers in the buckets' `tresCommas` slot.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQuery } from '@tanstack/react-query';
import type { TrackerId } from '@trafficpulse/shared';

import api from '@/services/api';
import type {
  DashboardDateRange,
  Granularity,
  TimeSeriesResponse,
} from '@/features/dashboard/compare/data/types';

export interface CountryTimeSeriesParams {
  countryKey: string;
  campaignId?: string;
  provider?: TrackerId;
  range: DashboardDateRange;
  granularity: Granularity;
  enabled: boolean;
}

export function useCountryTimeSeries({
  countryKey,
  campaignId,
  provider,
  range,
  granularity,
  enabled,
}: CountryTimeSeriesParams) {
  return useQuery({
    queryKey: [
      'smswiser',
      'timeseries',
      countryKey,
      campaignId ?? '',
      provider ?? '',
      range.from,
      range.to,
      granularity,
    ],
    queryFn: async (): Promise<TimeSeriesResponse> => {
      const { data } = await api.get<{ data: TimeSeriesResponse } | TimeSeriesResponse>(
        '/dashboard/timeseries',
        {
          params: {
            from: range.from,
            to: range.to,
            granularity,
            countryKey,
            ...(campaignId ? { campaignId } : {}),
            ...(provider ? { provider } : {}),
          },
        },
      );
      const payload =
        data && typeof data === 'object' && 'data' in data
          ? (data as { data: TimeSeriesResponse }).data
          : (data as TimeSeriesResponse);
      return payload ?? { granularity, from: range.from, to: range.to, buckets: [] };
    },
    enabled: enabled && Boolean(range.from && range.to),
    staleTime: 2 * 60 * 1000,
    retry: 1,
    retryDelay: 3000,
  });
}
